import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Navbar from './navbar'
import AddFolder from '../new-folder/AddFolder'
import { folderService } from '../../../services/folderService'
import { COLORS } from '@/utils/colors'
import { useAuthStore } from '@/store/authStore'
import { AuthState } from '@/models/auth'

interface Folder {
  id: string;
  name: string;
  description: string;
  createdAt: string;
}

const DashboardPageLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isAddFolderOpen, setIsAddFolderOpen] = useState(false)
  const [folders, setFolders] = useState<Folder[]>([])
  const [loading, setLoading] = useState(false)
  const location = useLocation();
  const user = useAuthStore((state: AuthState) => state.user);
  const normalizedRole = user?.role?.trim().toLowerCase();

  // Determine if we are on the enclosure builder create page or view designs
  const isEnclosureBuilderCreate = location.pathname.startsWith('/dashboard/enclosure-builder');
  const isOrdersPage = location.pathname === '/dashboard/orders';

  // Show sidebar only for 'user' role and hide it on enclosure builder pages and orders page
  const showSidebar = normalizedRole === 'user' && !isEnclosureBuilderCreate && !isOrdersPage;

  // Load folders from localStorage on component mount
  useEffect(() => {
    const loadLocalFolders = () => {
      if (user?.role !== 'user') return;
      try {
        const savedFolders = localStorage.getItem('localFolders');
        if (savedFolders) {
          setFolders(JSON.parse(savedFolders));
        }
      } catch (error) {
        console.error('Error loading local folders:', error);
      }
    };

    loadLocalFolders();
  }, [user?.role]);

  useEffect(() => {
    const loadFolders = async () => {
      if (user?.role !== 'user') return;
      try {
        setLoading(true);
        const fetchedFolders: any = await folderService.getFolders();
        setFolders(fetchedFolders?.data);
        localStorage.setItem('localFolders', JSON.stringify(fetchedFolders?.data || []));
      } catch (error) {
        console.error('Error loading folders from API:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFolders();
  }, [user?.role]);


  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        // On desktop, force sidebar closed if on enclosure builder create
        if (isEnclosureBuilderCreate) {
          setSidebarOpen(false);
        }
      }
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [isEnclosureBuilderCreate])

  // Always close sidebar on mount if on enclosure builder create
  useEffect(() => {
    if (isEnclosureBuilderCreate) {
      setSidebarOpen(false);
    }
  }, [isEnclosureBuilderCreate]);

  const handleOpenAddFolder = () => setIsAddFolderOpen(true);
  const handleCloseAddFolder = () => setIsAddFolderOpen(false);

  const handleFolderCreated = (newFolder: any) => {

    // Extract the actual folder data from the API response
    const actualFolder = newFolder.data || newFolder;

    const currentFolders = Array.isArray(folders) ? folders : [];
    const updatedFolders = [...currentFolders, actualFolder];

    setFolders(updatedFolders);

    try {
      localStorage.setItem('localFolders', JSON.stringify(updatedFolders));
    } catch (error) {
      console.error('Error saving folders to localStorage:', error);
    }
  };



  const handleFolderUpdated = (id: string, updatedFolder: Folder) => {

    const updatedFolders = folders.map(folder =>
      folder.id === id ? updatedFolder : folder
    );

    setFolders(updatedFolders);
    localStorage.setItem('localFolders', JSON.stringify(updatedFolders));
  };


  return (
    <div
      style={{ backgroundColor: COLORS.background }}
      className="min-h-screen  h-full flex w-full overflow-hidden">

      {/* Mobile Header - Only visible on mobile and on /dashboard/enclosure-builder/create */}
      {/* {(isEnclosureBuilderCreate || window.innerWidth < 1024) && (
        <div className={`${isEnclosureBuilderCreate ? 'fixed' : 'lg:hidden fixed'} top-0 left-0 right-0 z-30 flex items-center justify-between p-4 text-white bg-[#111] border-b border-gray-800`}>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
            <span className="font-medium">Dashboard</span>
          </div>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1 rounded-md hover:bg-gray-800">
            <FiMenu className="w-6 h-6" />
          </button>
        </div>
      )} */}

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      <Navbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} forceHamburger={isEnclosureBuilderCreate} />
      {/* Sidebar Component */}
      {showSidebar && (
        <Sidebar
          key={`sidebar-${folders.length}-${Date.now()}`} // Force re-render
          onOpenAddFolder={handleOpenAddFolder}
          folders={folders}
          onFolderDeleted={(id: string) => {
            const updatedFolders = folders.filter(folder => folder.id !== id);
            setFolders(updatedFolders);
            localStorage.setItem('localFolders', JSON.stringify(updatedFolders));
          }}
          onFolderUpdated={handleFolderUpdated}
        />
      )}

      {/* Main Content Area */}
      <div
        className={`flex flex-col w-full mt-[50px] md:mt-[40px] lg:pt-0 ${showSidebar ? 'lg:pl-[270px]' : ''}`}>
        <Outlet />
      </div>

      {/* AddFolder Modal - Rendered at layout level */}
      <AddFolder
        isOpen={isAddFolderOpen}
        onClose={handleCloseAddFolder}
        onFolderCreated={handleFolderCreated}
      />
    </div>
  )
}

export default DashboardPageLayout
